import { useState, useRef, useEffect } from "react";
// We keep this, but we will make it optional in the logic below

type KeypointTuple = [number, number, number];

// GLOBAL SINGLETONS (Persist across component re-mounts)
// This prevents reloading the heavy model if the user navigates away and back.
let globalLoadingPromise: Promise<boolean> | null = null;
let globalDetector: any = null;
let globalPoseLib: any = null;

export default function useTensorFlow() {
  const [consecutiveFronts, setConsecutiveFronts] = useState(0);
  const [spinPhase, setSpinPhase] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const spinPhaseRef = useRef(0);
  const consecutiveFrontsRef = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true; // Reset on mount

    // 1. If already loaded globally, just use it.
    if (globalDetector) {
      setIsLoaded(true);
      return;
    }

    // 2. If not loading, start the process.
    if (!globalLoadingPromise) {
      globalLoadingPromise = loadDependenciesGlobal();
    }

    // 3. Wait for the global promise to resolve.
    globalLoadingPromise.then((success) => {
      if (success && mounted.current) {
        setIsLoaded(true);
      }
    });

    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Loads TensorFlow/MediaPipe dynamically.
   * This logic is ISOLATED from the server.
   */
  async function loadDependenciesGlobal(): Promise<boolean> {
    // GUARDRAIL 1: Strict Environment Check
    // Ensure we are in a browser environment with media capabilities
    if (
      typeof window === 'undefined' || 
      typeof navigator === 'undefined'
    ) {
      return false;
    }
    
    try {
      console.log("Starting TensorFlow preload...");
      
      // GUARDRAIL 2: Dynamic Imports
      // These heavy modules are only fetched by the browser, never the server.
      const [poseLib, tfjsCore, tfjsBackend] = await Promise.all([
        import("@tensorflow-models/pose-detection"),
        import("@tensorflow/tfjs-core"),
        import("@tensorflow/tfjs-backend-webgl") // Triggers backend registration side-effect
      ]);
      
      globalPoseLib = poseLib;
      
      // Explicitly set the backend to WebGL for performance
      await tfjsCore.setBackend('webgl');
      await tfjsCore.ready();
      console.log("TensorFlow backend ready (WebGL)");

      // GUARDRAIL 3: Zero-Config Asset Loading
      // If TENSORFLOW_SOLUTION_PATH fails or is missing, we must fallback 
      // to a CDN so the user doesn't have to copy files manually.
      const modelConfig = {
        runtime: "mediapipe",
        modelType: "full",
        solutionPath:"https://cdn.jsdelivr.net/npm/@mediapipe/pose", // Fallback for Zero Config
      } as const;

      try {
        globalDetector = await globalPoseLib.createDetector(
          globalPoseLib.SupportedModels.BlazePose,
          modelConfig
        );
        console.log("MediaPipe detector created successfully");
      } catch (mediapipeError) {
        console.warn("MediaPipe failed, falling back to TFJS runtime:", mediapipeError);
        
        // Fallback to TFJS runtime (slower but works without external assets)
        globalDetector = await globalPoseLib.createDetector(
          globalPoseLib.SupportedModels.BlazePose,
          {
            runtime: "tfjs",
            modelType: "full",
          }
        );
        console.log("TFJS detector created successfully");
      }
      
      return true;
    } catch (error) {
      console.error("Failed to load TensorFlow dependencies:", error);
      globalLoadingPromise = null; // Reset so retry is possible
      return false;
    }
  }

  function isFrontFrame(body: KeypointTuple[]) {
    // Guardrail: Ensure points exist before filtering
    if (!body || body.length === 0) return false;
    return body.filter((p) => p[2] > 0.7).length === 22;
  }

  const poseDetector = async (callback: () => void, webcamRef: any) => {
    // GUARDRAIL 4: Runtime Safety
    // Check if everything is truly ready before calculating
    if (!globalDetector || !mounted.current || !webcamRef?.current?.video) {
      return;
    }
    
    // Check if video is actually playing and has data
    const video = webcamRef.current.video;
    if (video.readyState < 2) return; 

    try {
      const poses = await globalDetector.estimatePoses(
        video,
        { flipHorizontal: false }
      );

      if (!poses || !poses.length) return;

      const keypoints = poses[0].keypoints;
      
      // Standardize keypoints
      const body: KeypointTuple[] = keypoints
        .slice(11) // Ignore face keypoints (0-10)
        .map((kp: any) => [
          kp.x > 0 && kp.x < 720 ? kp.x : -1,
          kp.y > 0 && kp.y < 1280 ? kp.y : -1,
          kp.score ?? 0,
        ]);

      if (spinPhaseRef.current === 0 && !isFrontFrame(body)) {
        setConsecutiveFronts((prev) => prev + 1);
      }

      if (spinPhaseRef.current === 1 && isFrontFrame(body)) {
        setConsecutiveFronts((prev) => prev + 1);
      }

      if (spinPhaseRef.current === 2) callback();
    } catch (err) {
      console.error("Pose detection error:", err);
    }
  };

  useEffect(() => {
    consecutiveFrontsRef.current = consecutiveFronts;
    if (consecutiveFronts > 6 && spinPhaseRef.current < 2) {
      setSpinPhase((s) => s + 1);
      setConsecutiveFronts(0);
    }
  }, [consecutiveFronts]);

  useEffect(() => {
    spinPhaseRef.current = spinPhase;
  }, [spinPhase]);

  const resetDetector = () => {
    spinPhaseRef.current = 0;
    consecutiveFrontsRef.current = 0;
    setConsecutiveFronts(0);
    setSpinPhase(0);
  };

  const retryLoading = async () => {
    if (!globalDetector && !globalLoadingPromise) {
      globalLoadingPromise = loadDependenciesGlobal();
      const success = await globalLoadingPromise;
      if (success && mounted.current) {
        setIsLoaded(true);
      }
    }
  };

  return { 
    poseDetector, 
    isLoaded,
    spinPhase,
    resetDetector,
    retryLoading
  };
}